// groupByNest.ts — dropdown grouping (CTSK-493 §5.4; design doc §3.3).
//
// Results are grouped by nest in first-appearance order, so the API's
// relevance ranking still decides which nest leads. A group header is
// rendered only when a nest has ≥2 hits (design doc: "if ≥2 results from
// the same nest, show a lightweight group header"); singletons render as
// a plain row carrying their own badge.
//
// `nest === null` (document carries no `nest/<slug>` master tag — seen on
// real data per the Phase 1a ship report §9(h)): choice (b) from the
// brief — un-nested results are NOT grouped and render flat AFTER every
// nest group, with no badge and no "No nest" header. Rationale: a header
// for the absence of a nest is noise, and keeping them last keeps the
// badge-bearing rows visually contiguous.
//
// `flat` is the exact visual order and is what keyboard ↑/↓ walks, so
// `aria-activedescendant` can never point at a row the eye wouldn't
// land on next.

import type { DocumentSearchNest, DocumentSearchResult } from '@captain-tasko/shared';

export interface NestGroup {
  nest: DocumentSearchNest;
  items: DocumentSearchResult[];
  /** True when a group header should render (≥2 items). */
  labelled: boolean;
}

export interface GroupedResults {
  groups: NestGroup[];
  unnested: DocumentSearchResult[];
  /** Visual order: every grouped item in group order, then unnested. */
  flat: DocumentSearchResult[];
}

export function groupByNest(results: readonly DocumentSearchResult[]): GroupedResults {
  const groups: NestGroup[] = [];
  const bySlug = new Map<string, NestGroup>();
  const unnested: DocumentSearchResult[] = [];
  for (const r of results) {
    if (!r.nest) { unnested.push(r); continue; }
    let g = bySlug.get(r.nest.slug);
    if (!g) {
      g = { nest: r.nest, items: [], labelled: false };
      bySlug.set(r.nest.slug, g);
      groups.push(g);
    }
    g.items.push(r);
  }
  for (const g of groups) g.labelled = g.items.length >= 2;
  const flat = [...groups.flatMap((g) => g.items), ...unnested];
  return { groups, unnested, flat };
}
